import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
// (2026-04-24, Jason Cox) App-level RFID scanner provider so the BLE
// connection persists across page transitions.
import { RfidScannerProvider } from './contexts/RfidScannerContext';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import TagItems from './pages/TagItems';
import ManageTags from './pages/ManageTags';
// (2026-04-16, Jason Cox) Manual Checkout page -- Smart-only, hand scanner driven.
import ManualCheckout from './pages/ManualCheckout';
// (2026-04-24, Jason Cox) Inventory page -- Smart-only, reads existing
// md/v1/listBaseRfid endpoint aggregated client-side by SKU.
import Inventory from './pages/Inventory';
// (2026-04-22, Jason Cox) Device Status page -- Smart-only, reads existing
// md/v1/listSmartBox endpoint (no backend changes).
import DeviceStatus from './pages/DeviceStatus';
// (2026-04-22, Jason Cox) Consumption Events (internally 'DeliveryTickets')
// -- Smart-only, reads existing scm/v1/listDeliverTicket endpoint.
import DeliveryTickets from './pages/DeliveryTickets';
// (2026-04-23, Jason Cox) Restock Events -- Smart-only, reads existing
// scm/v1/listTransferTicket endpoint.
import RestockEvents from './pages/RestockEvents';
import NotFound from './pages/NotFound';
import LoadingSpinner from './components/LoadingSpinner';
import { configService } from './services/configService';
import { ExclamationTriangleIcon, WifiIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      <Route 
        path="/" 
        element={<Navigate to="/dashboard" replace />} 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tag-items" 
        element={
          <ProtectedRoute>
            <TagItems />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/manage-tags"
        element={
          <ProtectedRoute>
            <ManageTags />
          </ProtectedRoute>
        }
      />
      {/* (2026-04-16, Jason Cox) Manual Checkout route. */}
      <Route
        path="/manual-checkout"
        element={
          <ProtectedRoute>
            <ManualCheckout />
          </ProtectedRoute>
        }
      />
      {/* (2026-04-24, Jason Cox) Inventory route. */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      {/* (2026-04-22, Jason Cox) Device Status route. */}
      <Route
        path="/device-status"
        element={
          <ProtectedRoute>
            <DeviceStatus />
          </ProtectedRoute>
        }
      />
      {/* (2026-04-22, Jason Cox) Consumption Events route (path kept as
          /delivery-tickets; UI label is "Consumption Events"). */}
      <Route
        path="/delivery-tickets"
        element={
          <ProtectedRoute>
            <DeliveryTickets />
          </ProtectedRoute>
        }
      />
      {/* (2026-04-23, Jason Cox) Restock Events route. */}
      <Route
        path="/restock-events"
        element={
          <ProtectedRoute>
            <RestockEvents />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App: React.FC = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineWarning, setShowOfflineWarning] = useState(!navigator.onLine);

  useEffect(() => {
    // Check internet connection on startup
    if (!navigator.onLine) {
      console.warn('⚠️ No internet connection detected on startup');
      setShowOfflineWarning(true);
    }

    // Set up connection monitoring
    const handleOnline = () => {
      console.log('✅ Internet connection restored');
      setIsOnline(true);
      setShowOfflineWarning(false);
    };

    const handleOffline = () => {
      console.error('❌ Internet connection lost');
      setIsOnline(false);
      setShowOfflineWarning(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load configuration
    configService.loadConfig()
      .then(() => {
        console.log('✅ Configuration loaded successfully');
        setConfigLoaded(true);
      })
      .catch((error) => {
        console.error('❌ Failed to load configuration:', error);
        setConfigError('Failed to load application configuration. Using default settings.');
        setConfigLoaded(true); // Continue with defaults
      });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show loading spinner while config is loading
  if (!configLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Show error message if config failed but continue with app
  if (configError) {
    console.warn(configError);
  }

  return (
    <Router basename={process.env.PUBLIC_URL && process.env.PUBLIC_URL.startsWith("/") ? process.env.PUBLIC_URL : "/"}>
      <AuthProvider>
        <RfidScannerProvider>
        <div className="App">
          {/* Offline Warning Banner */}
          {showOfflineWarning && (
            <div className="fixed top-0 left-0 right-0 z-50 bg-error-600 text-white px-4 py-3 shadow-lg">
              <div className="flex items-center justify-between max-w-7xl mx-auto">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="font-medium">No Internet Connection</span>
                  <span className="ml-2 text-sm">
                    This app requires an internet connection to function properly.
                  </span>
                </div>
                <button
                  onClick={() => setShowOfflineWarning(false)}
                  className="ml-4 text-white hover:text-error-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* Connection Status Indicator */}
          {!isOnline && (
            <div className="fixed bottom-4 right-4 z-50 bg-error-100 text-error-900 px-4 py-2 rounded-lg shadow-lg flex items-center">
              <WifiIcon className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Offline</span>
            </div>
          )}
          
          <AppRoutes />
        </div>
        </RfidScannerProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;